import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Skeleton,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useAppDispatch, useAppSelector } from "@/store";
import {
  fetchCategories,
  createCategory,
  updateCategory,
  deleteCategory,
} from "@/store/categoriesSlice";
import { ConfirmDialog } from "@components/ConfirmDialog";
import { useNotification } from "@context/NotificationContext";
import { extractErrorMessage } from "@api/axios";
import type { Category } from "@/types";

const schema = yup.object({ name: yup.string().required().min(1) });

export default function Categories() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { items, loading } = useAppSelector((s) => s.categories);
  const { notify } = useNotification();

  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Category | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Category | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  const filtered = items.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase()),
  );

  const openCreate = () => {
    setEditing(null);
    reset({ name: "" });
    setDialogOpen(true);
  };

  const openEdit = (cat: Category) => {
    setEditing(cat);
    reset({ name: cat.name });
    setDialogOpen(true);
  };

  const onSubmit = async (data: { name: string }) => {
    try {
      if (editing) {
        await dispatch(updateCategory({ id: editing.id, data })).unwrap();
        notify(t("categories.edit"), "success");
      } else {
        await dispatch(createCategory(data)).unwrap();
        notify(t("categories.add"), "success");
      }
      setDialogOpen(false);
    } catch (err) {
      notify(extractErrorMessage(err, t("common.unexpectedError")), "error");
    }
  };

  const onDelete = async () => {
    if (!deleteTarget) return;
    try {
      await dispatch(deleteCategory(deleteTarget.id)).unwrap();
      notify(t("common.delete"), "success");
    } catch (err) {
      notify(extractErrorMessage(err, t("common.unexpectedError")), "error");
    }
    setDeleteTarget(null);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
            }}
          >
            {t("categories.title")}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
            }}
          >
            {t("categories.subtitle")}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
        >
          {t("categories.add")}
        </Button>
      </Box>
      <TextField
        fullWidth
        size="small"
        placeholder={t("common.search")}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ my: 2 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          },
        }}
      />
      {loading ? (
        <Grid container spacing={2}>
          {[1, 2, 3].map((i) => (
            <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
              <Skeleton variant="rounded" height={80} />
            </Grid>
          ))}
        </Grid>
      ) : filtered.length === 0 ? (
        <Typography
          sx={{
            color: "text.secondary",
            mt: 4,
            textAlign: "center",
          }}
        >
          {t("common.noData")}
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {filtered.map((cat) => (
            <Grid key={cat.id} size={{ xs: 12, sm: 6, md: 4 }}>
              <Card>
                <CardContent
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    py: 2,
                    "&:last-child": { pb: 2 },
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    {cat.name}
                  </Typography>
                  <Box>
                    <IconButton size="small" onClick={() => openEdit(cat)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => setDeleteTarget(cat)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            {editing ? t("categories.edit") : t("categories.add")}
          </DialogTitle>
          <DialogContent>
            <TextField
              {...register("name")}
              label={t("categories.name")}
              placeholder={t("categories.namePlaceholder")}
              fullWidth
              error={!!errors.name}
              helperText={errors.name?.message}
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" variant="contained">
              {t("common.save")}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <ConfirmDialog
        open={!!deleteTarget}
        title={t("common.delete")}
        message={t("categories.deleteConfirm")}
        onConfirm={onDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
}
